'use client';
import { useIoTFieldData } from '@/domains/iot-v2/provider';
import { alarmWidgetMessage, productSensorMessages } from '@/domains/iot-v2/widget-events';
import {
  createBoardAlarmOccurrenceProjectionState,
  createBoardAlarmRealtimeAcceptanceState,
  normalizeBoardAlarmRealtimeEvent,
  reduceBoardAlarmOccurrenceProjection,
  reduceBoardAlarmRealtimeAcceptance,
  type BoardAlarmOccurrenceProjectionState,
  type BoardAlarmRealtimeAcceptanceState,
  type BoardAlarmRealtimeEvent,
} from '@/domains/realtime/board-alarm-realtime-event';
import {
  appendBoardRealtimeEvent,
  createBoardDeviceRealtimeProjectionState,
  normalizeBoardDeviceRealtimeEvent,
  reduceBoardDeviceRealtimeProjection,
  type BoardDeviceRealtimeEvent,
} from '@/domains/realtime/board-device-realtime-event';
import { applyAlarmListReconciliationSignal, applyAlarmMessage, createEmptyAlarmListSnapshot, type AlarmListSnapshot } from '@/domains/widgets/realtime-alarm-list-model';
import { type SseConnectionStatus } from '@/hooks/realtime/useBackendWsMessage';
import { isWsAlarmCountChangedMessage, WsEventType, type WsBaseMessage } from '@/shared/realtime/ws-types';
import { debugRealtimeAlarmStage } from '@/shared/realtime/realtime-alarm-debug';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
type BoardAlarmRealtimeSnapshot = {
  event: BoardAlarmRealtimeEvent | null;
  fieldUid: string;
  history: readonly BoardAlarmRealtimeEvent[];
  projection: readonly BoardAlarmRealtimeEvent[];
};
type BoardDeviceRealtimeSnapshot = {
  deviceEvent: BoardDeviceRealtimeEvent | null;
  deviceHistory: readonly BoardDeviceRealtimeEvent[];
  deviceProjection: readonly BoardDeviceRealtimeEvent[];
};
export type BoardAlarmRealtimeContextValue = BoardAlarmRealtimeSnapshot &
  BoardDeviceRealtimeSnapshot & {
    alarmListSnapshot: AlarmListSnapshot;
    clearAlarmListSnapshot: () => void;
    connectionRevision: number;
    connectionStatus: SseConnectionStatus;
    deviceConnectionStatus: SseConnectionStatus;
  };
const BoardAlarmRealtimeContext = createContext<BoardAlarmRealtimeContextValue | null>(null);
function createAlarmSnapshot(fieldUid: string): BoardAlarmRealtimeSnapshot {
  return { event: null, fieldUid, history: [], projection: [] };
}
function createDeviceSnapshot(): BoardDeviceRealtimeSnapshot {
  return { deviceEvent: null, deviceHistory: [], deviceProjection: [] };
}
function ScopedBoardAlarmRealtimeProvider({ children, enabled = true, fieldUid = '' }: { children: ReactNode; enabled?: boolean; fieldUid?: string }) {
  const iot = useIoTFieldData();
  const v2Seen = useRef(new Map<string, number>());
  const normalizedFieldUid = fieldUid.trim();
  const alarmSequenceRef = useRef(0);
  const deviceSequenceRef = useRef(0);
  const alarmAcceptanceStateRef = useRef<BoardAlarmRealtimeAcceptanceState>(createBoardAlarmRealtimeAcceptanceState());
  const alarmOccurrenceStateRef = useRef<BoardAlarmOccurrenceProjectionState>(createBoardAlarmOccurrenceProjectionState());
  const deviceProjectionStateRef = useRef(createBoardDeviceRealtimeProjectionState());
  const latestDeviceEventSentAtRef = useRef(new Map<string, number>());
  const seenDeviceEventIdsRef = useRef(new Set<string>());
  const transportErrorSignatureRef = useRef(new Map<string, string>());
  const lastAlarmConnectionStatusRef = useRef<SseConnectionStatus>('closed');
  const [connectionRevision, setConnectionRevision] = useState(0);
  const lastV2ConnectionStatusRef = useRef(iot.connectionStatus);
  useEffect(() => {
    if (iot.connectionStatus === 'CONNECTED' && lastV2ConnectionStatusRef.current !== 'CONNECTED') setConnectionRevision(value => value + 1);
    lastV2ConnectionStatusRef.current = iot.connectionStatus;
  }, [iot.connectionStatus]);
  const [alarmSnapshot, setAlarmSnapshot] = useState<BoardAlarmRealtimeSnapshot>(() => createAlarmSnapshot(normalizedFieldUid));
  const [deviceSnapshot, setDeviceSnapshot] = useState<BoardDeviceRealtimeSnapshot>(createDeviceSnapshot);
  const [alarmListSnapshot, setAlarmListSnapshot] = useState<AlarmListSnapshot>(createEmptyAlarmListSnapshot);
  const acceptAlarmDelivery = useCallback(
    (message: WsBaseMessage) => {
      const event = normalizeBoardAlarmRealtimeEvent(message, alarmSequenceRef.current + 1);
      if (!event || event.fieldUid !== normalizedFieldUid || event.transition === 'ignore') return;
      const acceptance = reduceBoardAlarmRealtimeAcceptance(alarmAcceptanceStateRef.current, event);
      if (!acceptance.accepted) return;
      alarmAcceptanceStateRef.current = acceptance.state;
      alarmSequenceRef.current = event.sequence;
      debugRealtimeAlarmStage(event.deliverySource === 'device' ? 'message_received' : 'alarm_received', {
        correlation_id: event.correlationId || null,
        delivery_source: event.deliverySource,
        event_at_ms: event.eventAtMs,
        field_uid: event.fieldUid,
        occurrence_id: event.occurrenceId || null,
        received_at_ms: event.receivedAtMs,
      });
      const occurrence = reduceBoardAlarmOccurrenceProjection(alarmOccurrenceStateRef.current, event);
      if (occurrence.triggerEvent?.transition === 'activate') {
        debugRealtimeAlarmStage('occurrence_created', {
          correlation_id: occurrence.triggerEvent.correlationId || null,
          field_uid: occurrence.triggerEvent.fieldUid,
          occurrence_id: occurrence.triggerEvent.occurrenceId,
        });
      } else if (event.deliverySource === 'persisted' && occurrence.updatedEvent) {
        debugRealtimeAlarmStage('alarm_enriched', {
          alarm_uid: occurrence.updatedEvent.alarmUid || null,
          correlation_id: occurrence.updatedEvent.correlationId || null,
          field_uid: occurrence.updatedEvent.fieldUid,
          occurrence_id: occurrence.updatedEvent.occurrenceId,
        });
      }
      setAlarmSnapshot(current => ({
        event: occurrence.triggerEvent ?? (current.fieldUid === normalizedFieldUid ? current.event : null),
        fieldUid: normalizedFieldUid,
        history: appendBoardRealtimeEvent(current.fieldUid === normalizedFieldUid ? current.history : [], event),
        projection: occurrence.projection,
      }));
    },
    [normalizedFieldUid]
  );
  const handleDeviceMessage = useCallback(
    (message: WsBaseMessage) => {
      if (String(message.event).startsWith('SACON_PRODUCT_') && (message.body as Record<string, unknown>)?.provider !== 'V2') return;
      const event = normalizeBoardDeviceRealtimeEvent(message, deviceSequenceRef.current + 1);
      if (!event || event.fieldUid !== normalizedFieldUid) return;
      const deviceUid = event.sourceDeviceUid || event.displayDeviceUid;
      const eventFamily = message.event === WsEventType.ISSUED_ASSET_STATUS ? WsEventType.ISSUED_ASSET_STATUS : 'SACON_DEVICE_STATE';
      const streamKey = deviceUid ? `${event.fieldUid}:${eventFamily}:${deviceUid}` : event.eventId;
      const latestSentAt = latestDeviceEventSentAtRef.current.get(streamKey);
      if (seenDeviceEventIdsRef.current.has(event.eventId) || (latestSentAt !== undefined && event.sentAtMs < latestSentAt)) return;
      if (seenDeviceEventIdsRef.current.size >= 512) {
        const oldestEventId = seenDeviceEventIdsRef.current.values().next().value;
        if (oldestEventId) seenDeviceEventIdsRef.current.delete(oldestEventId);
      }
      seenDeviceEventIdsRef.current.add(event.eventId);
      latestDeviceEventSentAtRef.current.set(streamKey, event.sentAtMs);
      deviceSequenceRef.current = event.sequence;
      setDeviceSnapshot(current => ({
        deviceEvent: event,
        deviceHistory: appendBoardRealtimeEvent(current.deviceHistory, event),
        deviceProjection: reduceBoardDeviceRealtimeProjection(deviceProjectionStateRef.current, event),
      }));
      if ((message.body as Record<string, unknown>)?.provider !== 'V2') acceptAlarmDelivery(message);
    },
    [acceptAlarmDelivery, normalizedFieldUid]
  );
  const handleAlarmMessage = useCallback(
    (message: WsBaseMessage) => {
      if (String(message.event).startsWith('SACON_PRODUCT_') && (message.body as Record<string, unknown>)?.provider !== 'V2') return;
      setAlarmListSnapshot(current => (isWsAlarmCountChangedMessage(message) ? applyAlarmListReconciliationSignal(current) : applyAlarmMessage(current, message)));
      acceptAlarmDelivery(message);
    },
    [acceptAlarmDelivery]
  );
  useEffect(() => {
    alarmSequenceRef.current = 0;
    deviceSequenceRef.current = 0;
    alarmAcceptanceStateRef.current = createBoardAlarmRealtimeAcceptanceState();
    alarmOccurrenceStateRef.current = createBoardAlarmOccurrenceProjectionState();
    deviceProjectionStateRef.current = createBoardDeviceRealtimeProjectionState();
    latestDeviceEventSentAtRef.current.clear();
    seenDeviceEventIdsRef.current.clear();
    transportErrorSignatureRef.current.clear();
    lastAlarmConnectionStatusRef.current = 'closed';
  }, [normalizedFieldUid]);
  useEffect(() => {
    v2Seen.current.clear();
  }, [normalizedFieldUid, iot.provider, iot.routingVersion, iot.ownershipEpoch]);
  useEffect(() => {
    if (!enabled || iot.loadStatus !== 'READY') return;
    for (const product of Object.values(iot.productsById)) {
      if (v2Seen.current.get(product.runtime_key) === product.version) continue;
      v2Seen.current.set(product.runtime_key, product.version);
      for (const message of productSensorMessages(product)) handleDeviceMessage(message);
    }
    for (const alarm of Object.values(iot.alarmsByRuntimeKey)) {
      if (v2Seen.current.get(alarm.runtime_key) === alarm.version) continue;
      v2Seen.current.set(alarm.runtime_key, alarm.version);
      const isInitialSnapshot = iot.snapshotAlarmVersionsByRuntimeKey[alarm.runtime_key] === alarm.version;
      handleAlarmMessage(alarmWidgetMessage(alarm, iot.productsById[`product:${alarm.product_instance_id}`], isInitialSnapshot));
    }
  }, [enabled, iot.provider, iot.loadStatus, iot.productsById, iot.alarmsByRuntimeKey, iot.snapshotAlarmVersionsByRuntimeKey, handleDeviceMessage, handleAlarmMessage]);
  const currentAlarmSnapshot = useMemo<BoardAlarmRealtimeSnapshot>(
    () => (alarmSnapshot.fieldUid === normalizedFieldUid ? alarmSnapshot : createAlarmSnapshot(normalizedFieldUid)),
    [alarmSnapshot, normalizedFieldUid]
  );
  const clearAlarmListSnapshot = useCallback(() => setAlarmListSnapshot(createEmptyAlarmListSnapshot()), []);
  const value = useMemo<BoardAlarmRealtimeContextValue>(
    () => ({
      ...currentAlarmSnapshot,
      ...deviceSnapshot,
      alarmListSnapshot,
      clearAlarmListSnapshot,
      connectionRevision,
      connectionStatus: iot.loadStatus === 'READY' && iot.connectionStatus === 'CONNECTED' ? 'connected' : 'connecting',
      deviceConnectionStatus: iot.loadStatus === 'READY' && iot.connectionStatus === 'CONNECTED' ? 'connected' : 'connecting',
    }),
    [currentAlarmSnapshot, deviceSnapshot, alarmListSnapshot, clearAlarmListSnapshot, connectionRevision, iot.loadStatus, iot.connectionStatus]
  );
  return <BoardAlarmRealtimeContext.Provider value={value}>{children}</BoardAlarmRealtimeContext.Provider>;
}
export function useBoardAlarmRealtime(): BoardAlarmRealtimeContextValue {
  const value = useContext(BoardAlarmRealtimeContext);
  if (!value) throw new Error('useBoardAlarmRealtime은 BoardAlarmRealtimeProvider 내부에서 사용해야 합니다.');
  return value;
}
/** 운영 버전·소유권·접근 권한이 바뀌면 이전 알림과 처리 대기 상태를 함께 비웁니다. */
export function BoardAlarmRealtimeProvider(props: Parameters<typeof ScopedBoardAlarmRealtimeProvider>[0]) {
  const iot = useIoTFieldData();
  const scope = `${iot.fieldUid}:${iot.provider}:${iot.routingVersion}:${iot.ownershipEpoch}:${iot.loadStatus === 'FORBIDDEN'}`;
  return <ScopedBoardAlarmRealtimeProvider key={scope} {...props} />;
}
